import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useResults } from '@/state/useResults';
import { useSession } from '@/state/session';
import { useData } from '@/state/data';
import { useDemoStore } from '@/state/store';
import { useMachineChartDays, useMachineResult } from '@/state/useChartDays';
import { can, roleLabel } from '@/domain/roles';
import { fmtNumber } from '@/domain/numbers';
import { formatDateShort, formatDateTime, formatOsloDate, formatOsloSpan, formatRange, osloDateOf, osloTimeOf, overlapHours } from '@/domain/time';
import { ACTIVITY_LABELS, MACHINE_TYPE_LABELS, QUALITY_LABELS } from '@/lib/labels';
import { machineLabel, presentText } from '@/lib/present';
import { KpiStrip, KpiStripCell, StripCell } from '@/components/app/Kpi';
import { MachineTrendChart, ShiftTimeline, TimelineLegend, CHART } from '@/components/app/Charts';
import { EmptyState, PageSkeleton, Panel, PanelHeader } from '@/components/app/States';
import { CoverageBadge, Note } from '@/components/app/Badges';
import { PageHeader, DefList } from '@/components/app/PageHeader';
import { ProvenanceList, useRecordViewer } from '@/components/app/RecordViewer';
import { MachineRegisterPanels } from '@/components/app/Maskinpark';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { periodRange } from '@/lib/driftApi';
import { cn } from '@/lib/utils';

const ASSUMED_IDLE_LPH = 6;

export default function MaskinDetaljPage() {
  const { id } = useParams();
  const r = useResults();
  const { role, session } = useSession();
  const { dataset } = useData();
  const store = useDemoStore();
  const { openRecord } = useRecordViewer();
  const machineResult = useMachineResult(r.dataset, r.query, id);
  const trendDays = useMachineChartDays(r.dataset, r.query, id);
  const [idleOpen, setIdleOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [params, setParams] = useSearchParams();
  // Registrert maskintid på maskinkortet følger driftssidenes standardperiode (siste 14 dager) – ikke produksjonsperioden.
  const driftPeriod = useMemo(() => periodRange('14', osloDateOf(new Date())), []);
  const tab = params.get('fane') === 'maskinpark' ? 'saker' : 'drift';
  const setTab = (v) => {
    const next = new URLSearchParams(params);
    if (v === 'saker') next.set('fane', 'maskinpark');
    else next.delete('fane');
    setParams(next, { replace: true });
  };

  const vis = r.sp.get('vis');
  useEffect(() => {
    if (vis === 'tomgang' && r.ready) {
      setIdleOpen(true);
      r.set({ vis: null }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vis, r.ready]);

  const machine = dataset?.machines.find((m) => m.machineId === id);
  const curResult = r.current;
  const ready = r.ready;
  const intervals = useMemo(() => {
    if (!ready || !machine || !curResult) return [];
    return dataset.machineIntervals.filter((m) => m.machineId === id && m.fromUtc >= curResult.fromUtc && m.fromUtc < curResult.toUtc).sort((a, b) => a.fromUtc.localeCompare(b.fromUtc));
  }, [ready, curResult, dataset, id, machine]);

  const idleIntervals = useMemo(() => {
    if (!ready || !curResult) return [];
    return intervals
      .filter((m) => m.idleHours !== null && m.idleHours > 0)
      .map((m) => {
        const overlappingStops = curResult.stops.filter((s) => overlapHours(m.fromUtc, m.toUtc, s.fromUtc, s.toUtc) > 0);
        return { ...m, overlappingStops, idlePct: m.engineHours ? (m.idleHours / m.engineHours) * 100 : null };
      })
      .sort((a, b) => b.idleHours - a.idleHours);
  }, [intervals, ready, curResult]);

  if (!r.ready) return <PageSkeleton />;
  if (!machine) {
    return <EmptyState type="no_results" title="Fant ikke maskinen" description={`Ingen maskin med ID «${machineLabel(id)}» i datasettet.`} action={<Button asChild variant="outline" size="sm"><Link to={r.linkTo('/maskiner')}>Til maskinoversikten</Link></Button>} />;
  }
  const agg = r.current.machines.find((m) => m.machineId === id);
  const mr = machineResult;
  const c = r.current;
  const label = machineLabel(machine.machineId);
  const commentsKey = `machine:${id}`;
  const comments = store.annotationsFor(commentsKey);
  const canComment = can(role, 'comment');
  const measuredIdleFuel = agg?.idleFuelMeasured;
  const estimatedIdleFuel = agg ? agg.idleHours * ASSUMED_IDLE_LPH : 0;
  const inScope = machine.inProductionScope;
  const days = c.days.filter((d) => d.kind !== 'planned_off');
  const showTimelines = days.length <= 7;
  const hot = agg?.idleShare !== null && agg?.idleShare > 25;

  const submitComment = (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    store.addComment({ targetId: commentsKey, text: comment.trim(), demoAuthor: session.demoUser, role, now: new Date().toISOString() });
    setComment('');
    toast.success('Kommentar lagret', { description: 'Lagret lokalt i denne nettleseren.' });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: 'Maskiner', to: r.linkTo('/maskiner'), testId: 'back-to-machines-link' }, { label: `${label} · ${machine.fictionalName}` }]}
        title={
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="hr-num rounded-[8px] border border-[var(--hr-hairline)] bg-white px-2 py-0.5 text-[16px] font-semibold tracking-tight text-[var(--hr-heading)]">{label}</span>
            <span>{machine.fictionalName}</span>
          </span>
        }
        lead={`${MACHINE_TYPE_LABELS[machine.type]} · ${ACTIVITY_LABELS[machine.defaultActivity]} · ${formatRange(r.fromDate, r.toDate)}${inScope ? '' : ' · utenfor produksjonsområdet'}`}
      >
        {agg && <CoverageBadge status={agg.quality === 'complete' ? 'complete' : agg.quality === 'partial' ? 'partial' : 'not_applicable'} label={agg.quality === 'complete' ? 'Komplett grunnlag' : agg.quality === 'partial' ? `Delvis: ${agg.missingCount} felt mangler` : 'Ingen data i perioden'} />}
        <Button variant="outline" size="sm" className="h-9 rounded-[10px] bg-white" onClick={() => setIdleOpen(true)} data-testid="open-idle-intervals-button">
          Tomgangsintervaller <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </PageHeader>

      {!inScope && <Note tone="amber">Maskinen brukes til {ACTIVITY_LABELS[machine.defaultActivity].toLowerCase()} og inngår ikke i produksjonsforholdstallene.</Note>}
      {agg?.missingFields.length > 0 && (
        <Note tone="amber" data-testid="machine-missing-note">
          Ufullstendig grunnlag: {agg.missingFields.join(', ')} mangler i {agg.missingCount} intervall. Manglende poster er merket i kildemålingene.
        </Note>
      )}

      {/* Maskinkort (status, saker, service) øverst – telematikk/drift i perioden under fanen «Drift i perioden». */}
      <MachineRegisterPanels
        machineId={id}
        from={driftPeriod.from}
        to={driftPeriod.to}
        periodLabel="siste 14 dager"
        showCases={tab === 'saker'}
        middle={
          <>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="h-10 rounded-[10px] bg-[var(--hr-surface-2)] p-1" data-testid="machine-detail-tabs">
                <TabsTrigger value="drift" className="h-8 rounded-[8px] px-4 text-[14px] data-[state=active]:bg-white data-[state=active]:shadow-[var(--hr-shadow-xs)]" data-testid="machine-tab-drift">
                  Drift i perioden
                </TabsTrigger>
                <TabsTrigger value="saker" className="h-8 rounded-[8px] px-4 text-[14px] data-[state=active]:bg-white data-[state=active]:shadow-[var(--hr-shadow-xs)]" data-testid="machine-tab-saker">
                  Saker og service
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {tab === 'drift' && (
              <>
      {agg && (inScope || r.scope === 'all') && (
                  <KpiStrip data-testid="machine-kpi-row">
                    <StripCell label="Motortimer" value={fmtNumber(agg.engineHours, 2)} unit="h" meta={`${agg.intervalIds.length} intervaller · tomgang inngår`} valueTestId="machine-kpi-engine" spark={trendDays.map((d) => d.engineHours)} sparkLabel="Motortimer per dag" />
                    <StripCell
                      label="Tomgang"
                      value={fmtNumber(agg.idleHours, 2)}
                      unit="h"
                      meta={agg.idleShare === null ? 'Tomgangsandel utilgjengelig' : `${fmtNumber(agg.idleShare, 1)} % av egne motortimer${hot ? ' · over 25 %' : ''} · klikk for intervaller`}
                      metaTone={hot ? 'amber' : undefined}
                      onClick={() => setIdleOpen(true)}
                      testId="machine-kpi-idle-button"
                      valueTestId="machine-kpi-idle"
                      spark={trendDays.map((d) => d.idleShare)}
                      sparkColor="#234090"
                      sparkLabel="Tomgangsandel per dag"
                    />
                    <StripCell label="Diesel" value={fmtNumber(agg.fuelLitres, 1)} unit="L" meta={agg.missingFields.includes('fuelLitres') ? 'Registrert, delvis' : agg.fuelShare !== null ? `${fmtNumber(agg.fuelShare, 1)} % av tilordnet diesel` : 'Andel utilgjengelig'} metaTone={agg.missingFields.includes('fuelLitres') ? 'amber' : undefined} valueTestId="machine-kpi-fuel" spark={trendDays.map((d) => (d.fuelComplete ? d.fuelLitres : null))} sparkColor={CHART.bar} sparkLabel="Diesel per dag" />
                    {mr && <KpiStripCell metric={mr.dieselPerTonne} testKey="machine-fuel-per-total-tonne" />}
                  </KpiStrip>
                )}

                <div className="grid gap-5 lg:grid-cols-12">
                  <div className="min-w-0 lg:col-span-8">
                    <MachineTrendChart days={trendDays} selectedDates={c.dates} onSelectDay={(d) => r.setPeriod(d, d)} />
                  </div>
                  <Panel className="min-w-0 self-start lg:col-span-4" data-testid="idle-fuel-panel">
                    <PanelHeader title="Tomgangsdrivstoff" description="Målt verdi mot anslag." />
                    <div className="space-y-4 px-5 py-4">
                      <DefList
                        cols={1}
                        items={[
                          { label: 'Målt (kilde: idleFuelLitres)', value: measuredIdleFuel === null || measuredIdleFuel === undefined ? 'Ikke målt for denne maskinen' : `${fmtNumber(measuredIdleFuel, 1)} L`, testId: 'idle-fuel-measured' },
                          { label: `Anslag: tomgangstimer × ${ASSUMED_IDLE_LPH} L/h (antatt sats)`, value: agg ? `${fmtNumber(estimatedIdleFuel, 1)} L` : '–', className: 'text-[var(--hr-amber)]', testId: 'idle-fuel-estimated' },
                        ]}
                      />
                      <p className="text-[13px] leading-5 text-[var(--hr-muted)]">Anslaget er en størrelsesorden, ikke en måling. Målt tomgangsdrivstoff finnes kun for gravemaskinene.</p>
                    </div>
                  </Panel>
                </div>

                {showTimelines && days.length > 0 && (
                  <Panel data-testid="machine-timeline-panel">
                    <PanelHeader title="Tidslinje mot stopp i knuseverket" description="Samtidighet, ikke årsak. Klikk et intervall for kildeposten." />
                    <div className="space-y-5 px-5 py-4">
                      {days.map((d, i) => (
                        <div key={d.date} className="space-y-1.5">
                          <div className="hr-tabular text-sm font-medium text-[var(--hr-heading)]">{formatDateShort(d.date)}</div>
                          <ShiftTimeline date={d.date} lineStates={c.lineStates.filter((s) => osloDateOf(s.fromUtc) === d.date)} slots={intervals.filter((m) => osloDateOf(m.fromUtc) === d.date)} onSlotClick={(mi) => openRecord(mi.id)} compact={i !== 0} machineLabel={label} />
                        </div>
                      ))}
                      <TimelineLegend withMachine />
                    </div>
                  </Panel>
                )}
                {!showTimelines && <Note>Tidslinjen vises for perioder på inntil 7 produksjonsdager. Klikk en dag i trendgrafen for å se den.</Note>}

                <div className="grid gap-5 lg:grid-cols-12">
                  <Panel className="min-w-0 lg:col-span-8" data-testid="machine-intervals-panel">
                    <PanelHeader title="Kildemålinger" description={`${intervals.length} intervaller fra Maskindata · klikk en rad for råverdier`} />
                    <div className="hr-scroll max-h-[460px] overflow-auto">
                      <Table data-testid="machine-intervals-table">
                        <TableHeader className="sticky top-0 z-[1] bg-white">
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="pl-5">Dato</TableHead>
                            <TableHead>Tidsrom (Oslo)</TableHead>
                            <TableHead className="text-right">Motor (h)</TableHead>
                            <TableHead className="text-right">Tomgang (h)</TableHead>
                            <TableHead className="text-right">Diesel (L)</TableHead>
                            <TableHead className="hidden md:table-cell">Kvalitet</TableHead>
                            <TableHead className="hidden pr-5 lg:table-cell">Kildepost</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {intervals.map((m) => (
                            <TableRow key={m.id} className={cn('cursor-pointer', m.fuelLitres === null && 'bg-[var(--hr-amber-bg)]/50')} onClick={() => openRecord(m.id)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && openRecord(m.id)} data-testid={`interval-row-${m.id}`}>
                              <TableCell className="hr-tabular pl-5">{formatOsloDate(m.fromUtc)}</TableCell>
                              <TableCell className="hr-tabular">{osloTimeOf(m.fromUtc)}–{osloTimeOf(m.toUtc)}</TableCell>
                              <TableCell className="hr-num text-right">{m.engineHours === null ? <span className="font-sans text-[var(--hr-amber)]">mangler</span> : fmtNumber(m.engineHours, 3)}</TableCell>
                              <TableCell className="hr-num text-right">{m.idleHours === null ? <span className="font-sans text-[var(--hr-amber)]">mangler</span> : fmtNumber(m.idleHours, 3)}</TableCell>
                              <TableCell className="hr-num text-right">{m.fuelLitres === null ? <span className="font-sans font-medium text-[var(--hr-amber)]">mangler (null)</span> : fmtNumber(m.fuelLitres, 2)}</TableCell>
                              <TableCell className="hidden text-[13px] md:table-cell">{QUALITY_LABELS[m.quality]}{m.revision > 1 ? ` · rev. ${m.revision}` : ''}</TableCell>
                              <TableCell className="hidden pr-5 font-mono text-[13px] lg:table-cell">{presentText(m.sourceRecordId)}</TableCell>
                            </TableRow>
                          ))}
                          {intervals.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={7} className="py-8 text-center text-sm text-[var(--hr-muted)]">Ingen målinger i perioden.</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </Panel>

                  <Panel className="min-w-0 self-start lg:col-span-4" data-testid="machine-comments-panel">
                    <PanelHeader title="Kommentarer" />
                    <div className="space-y-4 px-5 py-4">
                      {comments.length === 0 && <p className="text-sm text-[var(--hr-muted)]">Ingen kommentarer ennå.</p>}
                      <ul className="space-y-3">
                        {[...comments].reverse().map((a) => (
                          <li key={a.id} className="flex gap-3 text-sm" data-testid={`machine-comment-${a.id}`}>
                            <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-[var(--hr-muted)]" aria-hidden="true" />
                            <div>
                              <div className="text-[13px] text-[var(--hr-muted)]">{a.demoAuthor} ({roleLabel(a.role)}) · {formatDateTime(a.createdAt)}</div>
                              <div className="leading-6">{a.text}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                      {canComment ? (
                        <form onSubmit={submitComment} className="space-y-2">
                          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Skriv en kommentar …" aria-label="Kommentar" className="bg-white" data-testid="machine-comment-input" />
                          <Button type="submit" size="sm" disabled={!comment.trim()} data-testid="machine-comment-submit">Lagre kommentar</Button>
                        </form>
                      ) : (
                        <p className="text-sm text-[var(--hr-muted)]">Rollen {roleLabel(role)} kan ikke legge inn kommentarer.</p>
                      )}
                    </div>
                  </Panel>
                </div>
              </>
            )}
          </>
        }
      />

      <Dialog open={idleOpen} onOpenChange={setIdleOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="idle-intervals-dialog">
          <DialogHeader>
            <DialogTitle>Tomgangsintervaller – {label}</DialogTitle>
            <DialogDescription>
              {agg ? `${fmtNumber(agg.idleHours, 2)} h tomgang i ${idleIntervals.length} intervaller, ${formatRange(r.fromDate, r.toDate)}.` : ''} Overlappende stopp i knuseverket er listet som «sammenfaller med».
            </DialogDescription>
          </DialogHeader>
          <Table data-testid="idle-intervals-table">
            <TableHeader>
              <TableRow>
                <TableHead>Tidsrom (Oslo)</TableHead>
                <TableHead className="text-right">Tomgang (h)</TableHead>
                <TableHead className="text-right">Andel</TableHead>
                <TableHead>Sammenfaller med</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {idleIntervals.map((m) => (
                <TableRow key={m.id} className="cursor-pointer" onClick={() => openRecord(m.id)} data-testid={`idle-interval-row-${m.id}`}>
                  <TableCell className="hr-tabular">{formatOsloSpan(m.fromUtc, m.toUtc)}</TableCell>
                  <TableCell className="hr-num text-right">{fmtNumber(m.idleHours, 3)}</TableCell>
                  <TableCell className="hr-num text-right">{m.idlePct === null ? '–' : `${fmtNumber(m.idlePct, 0)} %`}</TableCell>
                  <TableCell className="text-[13px]">{m.overlappingStops.length ? m.overlappingStops.map((s) => `Stopp ${osloTimeOf(s.fromUtc)}–${osloTimeOf(s.toUtc)}${s.stopReason ? ` (${s.stopReason})` : ''}`).join('; ') : <span className="text-[var(--hr-muted)]">Ingen registrerte stopp</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <ProvenanceList recordIds={idleIntervals.map((m) => m.id)} label="Kildeposter" testId="idle-provenance" />
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setIdleOpen(false)} data-testid="idle-intervals-close">Lukk</Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
